import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { formatDate, cn } from "@/lib/utils";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { format } from "date-fns";
import { Booking } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/notifications/notification-bell";
import BookingDetails from "@/components/admin/booking-details";

export default function AdminDashboardPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [bookingFilter, setBookingFilter] = useState<string>("all");
  
  // Get auth status from hook
  const { user: admin, isLoading: adminLoading, logoutMutation } = useAuth();
  
  // Booking detail state
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  
  // Fetch all bookings
  const { data: bookings = [], isLoading: bookingsLoading } = useQuery<Booking[]>({
    queryKey: ['/api/admin/bookings'],
    enabled: !!admin
  });
  
  // Filter bookings based on selected filter
  const filteredBookings = useMemo(() => {
    const now = new Date();
    
    switch (bookingFilter) {
      case 'confirmed':
        return bookings.filter(booking => booking.status === 'confirmed');
      case 'cancelled':
        return bookings.filter(booking => booking.status === 'cancelled');
      case 'upcoming':
        return bookings.filter(booking => new Date(booking.date) > now);
      case 'past':
        return bookings.filter(booking => new Date(booking.date) < now);
      case 'all':
      default:
        return bookings;
    }
  }, [bookings, bookingFilter]);
  
  // Update booking status mutation
  const updateBookingStatusMutation = useMutation({
    mutationFn: (data: { id: number; status: 'confirmed' | 'cancelled' }) => {
      return apiRequest("PATCH", `/api/admin/bookings/${data.id}/status`, { status: data.status });
    },
    onSuccess: () => {
      toast({
        title: "Booking Updated",
        description: "The booking status has been updated successfully.",
      });
      // Refresh bookings
      queryClient.invalidateQueries({ queryKey: ['/api/admin/bookings'] });
      setSelectedBooking(null);
      setBookingDetailsOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update booking status. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Handle booking status update
  const handleUpdateBookingStatus = (id: number, status: 'confirmed' | 'cancelled') => {
    updateBookingStatusMutation.mutate({ id, status });
  };
  
  // Handle logout
  const handleLogout = () => {
    logoutMutation.mutate();
  };
  
  // Handle view booking details
  const handleViewBookingDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setBookingDetailsOpen(true);
  };
  
  if (adminLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin mb-4 h-12 w-12 border-4 border-[#7d9b76] border-t-transparent rounded-full mx-auto"></div>
          <p className="text-[#1a365d] font-medium">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="bg-[#1a365d] text-white">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <Link href="/" className="text-xl font-serif font-bold">
              SW <span className="text-[#7d9b76]">Accounting</span>
            </Link>
            <div className="ml-8 hidden md:block">Admin Dashboard</div>
          </div>
          <div className="flex items-center gap-4">
            {admin && (
              <>
                <div className="hidden md:flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[#7d9b76] text-white">
                      {admin.username.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{admin.username}</span>
                </div>
                <NotificationBell />
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-[#7d9b76] hover:bg-transparent"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>
                  <span className="hidden md:inline">Logout</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-[#1a365d]">Admin Dashboard</h1>
            <p className="text-[#343a40]">Manage appointment bookings and client meetings.</p>
          </div>
          
          <div className="mt-4 md:mt-0">
            <Select onValueChange={(value) => setBookingFilter(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter bookings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="past">Past</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{bookings.length}</div>
              <p className="text-sm text-muted-foreground">All appointment bookings</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Confirmed Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {bookings.filter(booking => booking.status === 'confirmed').length}
              </div>
              <p className="text-sm text-muted-foreground">Ready for meeting</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Upcoming Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {bookings.filter(booking => new Date(booking.date) > new Date()).length}
              </div>
              <p className="text-sm text-muted-foreground">Future appointments</p>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="grid grid-cols-1 mb-8">
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="bookings" className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl font-bold text-[#1a365d]">Appointment Bookings</h2>
            </div>
            
            {bookingsLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#7d9b76] border-t-transparent rounded-full"></div>
              </div>
            ) : filteredBookings.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{booking.name}</div>
                            <div className="text-sm text-muted-foreground truncate max-w-[180px]">{booking.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{booking.service}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-[180px]">{booking.business}</div>
                        </TableCell>
                        <TableCell>
                          <div>{format(new Date(booking.date), 'MMMM d, yyyy')}</div>
                          <div className="text-sm text-muted-foreground">{booking.time}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(
                            "capitalize",
                            booking.status === "confirmed" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                            booking.status === "cancelled" ? "bg-red-100 text-red-800 hover:bg-red-100" :
                            "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                          )}>
                            {booking.status || "pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="mr-2"
                            onClick={() => handleViewBookingDetails(booking)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No bookings found matching your filter criteria.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Booking Details Dialog */}
      <Dialog open={bookingDetailsOpen} onOpenChange={setBookingDetailsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-[#1a365d]">
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <BookingDetails booking={selectedBooking} />
              
              {selectedBooking.status !== 'confirmed' && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'confirmed')}
                    disabled={updateBookingStatusMutation.isPending}
                  >
                    {updateBookingStatusMutation.isPending ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Processing...
                      </>
                    ) : (
                      'Confirm Booking'
                    )}
                  </Button>
                  
                  {selectedBooking.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      className="text-red-500 border-red-500 hover:bg-red-50"
                      onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'cancelled')}
                      disabled={updateBookingStatusMutation.isPending}
                    >
                      {updateBookingStatusMutation.isPending ? 'Processing...' : 'Cancel Booking'}
                    </Button>
                  )}
                </div>
              )}
              
              {selectedBooking.status === 'cancelled' && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'confirmed')}
                    disabled={updateBookingStatusMutation.isPending}
                  >
                    {updateBookingStatusMutation.isPending ? 'Processing...' : 'Reactivate Booking'}
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button variant="secondary">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}